/** Les genres de périphériques qu'on choisit en pleine séance. */
export type RoomDeviceKind = "audioinput" | "audiooutput" | "videoinput"

/** Un périphérique, tel que le navigateur le nomme. */
export type RoomDevice = { id: string; label: string }

/** Les appareils d'un genre : la liste, l'actif, et le geste qui change. */
export type RoomDeviceChoice = {
    devices: RoomDevice[]
    activeId: string | null
    select: (id: string) => void
}

/** Les trois genres que l'ancienne barre laissait choisir en pleine séance (le web seulement). */
export type RoomDevices = { microphone: RoomDeviceChoice; camera: RoomDeviceChoice; speaker: RoomDeviceChoice }

/** La suppression des bruits de fond (Krisp), quand le navigateur la permet. */
export type NoiseFilter = { enabled: boolean; pending: boolean; setEnabled: (enabled: boolean) => void }

/**
 * Les entrées « système » du navigateur (`default`, `communications`) doublent un vrai périphérique :
 * l'ancien sélecteur (`MediaDeviceSelectM`) les retirait.
 */
export function withoutSystemAliases<T extends { deviceId: string }>(devices: T[]): T[] {
    return devices.filter((device) => !isSystemAlias(device.deviceId))
}

const isSystemAlias = (id: string) => id === "default" || id === "communications"

/**
 * L'appareil de la liste qui est vraiment actif. Tant que l'élève n'a rien choisi, la salle suit le
 * système et dit `default` : cette entrée est retirée de la liste, et rien n'y était coché. Elle double
 * un vrai appareil, celui de son groupe (`groupId`, comme le navigateur les apparie) ; sans groupe
 * commun, le premier, celui que la liste dit « Par défaut du système ».
 */
export function activeRealDeviceId(
    devices: { deviceId: string; groupId?: string }[],
    activeId: string | null | undefined
): string | null {
    if (activeId == null || activeId === "") return null
    if (!isSystemAlias(activeId)) return activeId
    const real = withoutSystemAliases(devices)
    const alias = devices.find((device) => device.deviceId === activeId)
    const twin = alias?.groupId ? real.find((device) => device.groupId === alias.groupId) : undefined
    return (twin ?? real[0])?.deviceId ?? null
}
